import React from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DOCS_PATH } from '../../config/paths';
import { docsNavSections } from '../../config/docsNav';
import { cronpayLogoSrc } from '../../config/brandAssets';
import ThemedSelect from '../../components/ui/ThemedSelect';
import { useTheme } from '../../context/ThemeContext';

const DocsLayout = () => {
    const { resolvedTheme } = useTheme();
    const logoSrc = cronpayLogoSrc(resolvedTheme);
    const location = useLocation();
    const navigate = useNavigate();
    const currentSlug = location.pathname.replace(`${DOCS_PATH}/`, '').replace(/\/$/, '') || 'welcome';

    return (
        <div className="min-h-screen w-full bg-zinc-50 text-zinc-900 dark:bg-[#09090b] dark:text-zinc-100">
            <header className="sticky top-0 z-30 border-b border-zinc-200/80 bg-white/90 backdrop-blur-md dark:border-zinc-800 dark:bg-zinc-950/90">
                <div className="flex h-14 w-full items-center justify-between gap-4 px-4 sm:px-6 lg:px-5">
                    <Link
                        to="/"
                        title="Back to CronPay home"
                        className="flex items-center gap-2.5 rounded-lg transition-opacity hover:opacity-90"
                    >
                        <img src={logoSrc} alt="CronPay" className="h-8 w-auto object-contain" />
                        <span className="hidden text-sm font-semibold text-zinc-500 dark:text-zinc-400 sm:inline">
                            Docs
                        </span>
                    </Link>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 transition hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-zinc-600"
                    >
                        <i className="fa-solid fa-arrow-left text-[10px]" />
                        Back to home
                    </Link>
                </div>
            </header>

            <div className="flex w-full">
                <aside className="hidden w-[220px] shrink-0 border-r border-zinc-200 bg-white/50 dark:border-zinc-800 dark:bg-zinc-950/30 lg:block">
                    <nav className="sticky top-14 max-h-[calc(100vh-3.5rem)] overflow-y-auto py-6 pl-3 pr-2">
                        {docsNavSections.map((section) => (
                            <div key={section.id} className="mb-7">
                                <p className="mb-1.5 pl-2 text-[10px] font-bold uppercase tracking-[0.16em] text-teal-700 dark:text-teal-500">
                                    {section.title}
                                </p>
                                <ul className="m-0 list-none space-y-0.5 p-0">
                                    {section.items.map((item) => (
                                        <li key={item.slug}>
                                            <NavLink
                                                to={`${DOCS_PATH}/${item.slug}`}
                                                className={({ isActive }) =>
                                                    `flex items-center gap-2 rounded-lg py-2 pl-2 pr-2.5 text-sm font-medium transition ${
                                                        isActive
                                                            ? 'bg-teal-50 text-teal-900 dark:bg-teal-950/50 dark:text-teal-200'
                                                            : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900/80 dark:hover:text-zinc-200'
                                                    }`
                                                }
                                            >
                                                <i
                                                    className={`${item.icon} w-4 shrink-0 text-center text-xs opacity-70`}
                                                />
                                                <span className="truncate">{item.label}</span>
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        <p className="mt-8 pl-2 text-[11px] text-zinc-400 dark:text-zinc-600">
                            Built on · {process.env.REACT_APP_CHAIN_NAME || 'Arc'}
                        </p>
                    </nav>
                </aside>

                <main className="min-w-0 flex-1">
                    <div className="border-b border-zinc-200 bg-white/50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-950/30 lg:hidden">
                        <label htmlFor="docs-mobile-nav" className="sr-only">
                            Documentation section
                        </label>
                        <ThemedSelect
                            id="docs-mobile-nav"
                            value={currentSlug}
                            onChange={(event) =>
                                navigate(`${DOCS_PATH}/${event.target.value}`)
                            }
                            options={docsNavSections.flatMap((section) =>
                                section.items.map((item) => ({
                                    value: item.slug,
                                    label: `${section.title}: ${item.label}`,
                                })),
                            )}
                        />
                    </div>

                    <div className="relative overflow-hidden">
                        <div className="pointer-events-none absolute inset-0 opacity-40 dark:opacity-30" aria-hidden>
                            <div className="landing-grid absolute inset-0 opacity-[0.05]" />
                            <div className="absolute -left-20 top-20 h-64 w-64 rounded-full bg-teal-400/20 blur-3xl" />
                            <div className="absolute right-0 top-40 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" />
                        </div>
                        <div className="relative mx-auto w-full max-w-4xl px-5 py-10 sm:px-8 sm:py-14 lg:px-10">
                            <Outlet />
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DocsLayout;
